import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, RefreshCw, Search } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  businessesApi,
  BusinessDetail,
  BusinessRow,
} from '../../api/businesses';
import { BvnProviders } from '../../components/ui/BvnProviders';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';

/**
 * The estate.
 *
 * Support could see businesses waiting on a KYB decision, and businesses
 * holding pooled money, and nothing that listed all of them — so "who is
 * this company and what is going on with them" had no screen behind it.
 *
 * **Read-only, deliberately.** Tier decisions belong on the KYB review
 * queue, which records who decided and why. A screen that could edit a
 * company's registration details would be a way to change what a compliance
 * record says after the fact, which is the opposite of what the record is
 * for.
 *
 * The detail drawer is ordered the way somebody actually asks: who are they,
 * where are they in verification, where is their money, then what has been
 * moving. The KYB ladder shown is the same one the customer sees — read from
 * the same service — so a support conversation never opens by reconciling
 * two different answers to "what's outstanding?".
 */

const naira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateOnly = (iso: string) =>
  new Date(iso).toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const kybVariant: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> =
  {
    verified: 'success',
    pending: 'warning',
    rejected: 'danger',
    unverified: 'neutral',
  };

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

const controlStyle: React.CSSProperties = {
  height: 38,
  padding: '0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
};

export const Businesses: React.FC = () => {
  const navigate = useNavigate();
  usePage('Businesses');
  const toast = useToast();

  const [rows, setRows] = useState<BusinessRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kybStatus, setKybStatus] = useState('');
  const [withoutAccount, setWithoutAccount] = useState(false);
  const [detail, setDetail] = useState<BusinessDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await businessesApi.list({
        search: search.trim() || undefined,
        kybStatus: kybStatus || undefined,
        withoutAccount: withoutAccount || undefined,
        page,
        limit: 25,
      });
      setRows(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(err, 'Failed to load businesses.');
    } finally {
      setLoading(false);
    }
  }, [search, kybStatus, withoutAccount, page, toast]);

  // Debounced, so typing a company name doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  async function open(row: BusinessRow) {
    setDetailLoading(true);
    try {
      setDetail(await businessesApi.get(row.id));
    } catch (err) {
      toast.error(err, 'Could not load that business.');
    } finally {
      setDetailLoading(false);
    }
  }

  const pages = Math.max(1, Math.ceil(total / 25));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <div style={{ position: 'relative', flex: '1 1 260px' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 11,
                top: 11,
                color: 'var(--neutral-500)',
              }}
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name, RC number, email or phone"
              style={{ ...controlStyle, width: '100%', paddingLeft: 32 }}
            />
          </div>

          <select
            value={kybStatus}
            onChange={(e) => {
              setKybStatus(e.target.value);
              setPage(1);
            }}
            style={{ ...controlStyle, cursor: 'pointer' }}
          >
            <option value="">All verification states</option>
            <option value="unverified">Unverified</option>
            <option value="pending">Pending review</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 13,
              color: 'var(--neutral-700)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={withoutAccount}
              onChange={(e) => {
                setWithoutAccount(e.target.checked);
                setPage(1);
              }}
            />
            No account yet
          </label>

          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={15} style={{ marginRight: 6 }} />
            Refresh
          </Button>
        </div>

        {loading && !rows ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !rows?.length ? (
          <EmptyState
            title="No businesses match."
            description={search ? 'Try a different search.' : undefined}
          />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: 900,
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {[
                      'Business',
                      'Verification',
                      'Balance',
                      'Account',
                      'Registered',
                      '',
                    ].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                          {b.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          {[b.rcNumber, b.email].filter(Boolean).join(' · ') ||
                            'No registration details'}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Badge variant={kybVariant[b.kybStatus] ?? 'neutral'}>
                            {b.kybStatus}
                          </Badge>
                          <span style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                            Tier {b.kybTier}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {naira(b.balance)}
                      </td>
                      <td style={td}>
                        {b.accountNumber ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              color: 'var(--neutral-700)',
                            }}
                          >
                            {b.accountNumber}
                            {/* Whose name it is in. A transfer out of an
                                individual account carries the director's
                                name, not the company's — which is the first
                                thing support needs when a payer asks who
                                paid them. */}
                            {b.accountKind && (
                              <Badge
                                variant={
                                  b.accountKind === 'corporate'
                                    ? 'success'
                                    : 'info'
                                }
                              >
                                {b.accountKind}
                              </Badge>
                            )}
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              fontSize: 12,
                              color: 'var(--neutral-600)',
                            }}
                          >
                            <Landmark size={13} />
                            No account yet
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, color: 'var(--neutral-600)', whiteSpace: 'nowrap' }}>
                        {dateOnly(b.createdAt)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {/* The verification review lives on its own page —
                            documents and identity checks do not fit a drawer,
                            and acting on them is deliberate work rather than
                            a glance. */}
                        <Button
                          variant="ghost"
                          onClick={() => navigate(`/businesses/${b.id}/kyb`)}
                        >
                          Verification
                        </Button>
                        <Button variant="ghost" onClick={() => void open(b)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 16,
                  fontSize: 13,
                  color: 'var(--neutral-600)',
                }}
              >
                <span>
                  {total} business{total === 1 ? '' : 'es'} · page {page} of {pages}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={page >= pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Drawer
        isOpen={detail !== null || detailLoading}
        onClose={() => setDetail(null)}
        title={detail?.registration.name ?? 'Loading…'}
        subtitle={detail?.registration.rcNumber ?? undefined}
        width={620}
      >
        {detailLoading && !detail ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : detail ? (
          <BusinessDetailBody detail={detail} />
        ) : null}
      </Drawer>
    </div>
  );
};

/** Ordered the way somebody asks: who are they, where are they in
 *  verification, where is their money, then what has been moving. */
function BusinessDetailBody({ detail }: { detail: BusinessDetail }) {
  const r = detail.registration;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Section title="Registration">
        <Facts
          rows={[
            ['Legal name', r.name],
            ['Trading name', r.tradingName],
            ['Type', r.type],
            ['Industry', r.industry],
            ['Size', r.sizeBand],
            ['RC number', r.rcNumber],
            ['TIN', r.tin],
            ['Address', r.registeredAddress],
            ['Email', r.email],
            ['Phone', r.phone],
            ['Contact', r.contactName],
            ['Uses PayIQ for', r.purposes?.join(', ') ?? null],
            ['Registered', dateOnly(r.createdAt)],
          ]}
        />
      </Section>

      <Section title="Signs in as">
        {detail.owner ? (
          <Facts
            rows={[
              ['Name', detail.owner.fullName],
              ['Email', detail.owner.email],
              ['Phone', detail.owner.phone],
              [
                'Last signed in',
                detail.owner.lastLoginAt
                  ? dateOnly(detail.owner.lastLoginAt)
                  : null,
              ],
              /*
               * Only shown while it is true. A permanent "Locked until —"
               * row trains whoever reads this screen to skip the line that
               * matters on the one call where it does.
               */
              ...(detail.owner.lockedUntil &&
              new Date(detail.owner.lockedUntil) > new Date()
                ? ([['Locked until', detail.owner.lockedUntil]] as [
                    string,
                    string | null,
                  ][])
                : []),
            ]}
          />
        ) : (
          <p style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
            {/* The login cascades with the business, so this is not a
                normal state — it means something is wrong. */}
            No login owns this business.
          </p>
        )}
      </Section>

      <Section title="Verification">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Badge variant={kybVariant[r.kybStatus] ?? 'neutral'}>{r.kybStatus}</Badge>
          <span style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
            {/*
              "Currently Tier 1" was not always true. `kybTier` has no zero,
              so a business that has answered nothing reads as tier 1 —
              which on this screen looked like it had passed a tier it had
              not started. The tier list knows better, so it is asked.

              Tier 1 is also what generates the account now, so a business
              that has not satisfied it has nowhere to be paid.
            */}
            {detail.kyb?.tiers?.[0]?.satisfied === false
              ? 'Tier 1 outstanding — no account yet'
              : `Currently Tier ${r.kybTier}`}
          </span>
        </div>
        {detail.kyb?.tiers?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {detail.kyb.tiers.map((t) => (
              <div
                key={t.tier}
                style={{
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <span>
                    Tier {t.tier} — {t.name}
                  </span>
                  <Badge variant={t.satisfied ? 'success' : 'neutral'}>
                    {t.satisfied ? 'Complete' : 'Outstanding'}
                  </Badge>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {t.requirements.map((req) => (
                    <span
                      key={req.key}
                      style={{
                        fontSize: 12,
                        padding: '3px 8px',
                        borderRadius: 999,
                        background:
                          req.status === 'done'
                            ? 'var(--green-100)'
                            : req.status === 'pending'
                              ? 'var(--warning-100)'
                              : 'var(--neutral-200)',
                        color: 'var(--neutral-800)',
                      }}
                    >
                      {req.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>
            The verification ladder couldn&apos;t be read for this business.
          </p>
        )}
      </Section>

      {/*
        The filing the claim was decided against.

        Nothing in this console could see it before, which left a reviewer
        deciding a held CAC claim without the document it was held against —
        the one thing the decision turns on. The claim queue shows a copy of
        the board and says nothing about when the register was read or who
        else it lists.
      */}
      <Section title="CAC filing">
        {detail.cacRegister ? (
          <>
            <Facts
              rows={[
                ['Approved name', detail.cacRegister.approvedName],
                ['RC number', detail.cacRegister.rcNumber],
                ['Type', detail.cacRegister.companyType],
                [
                  'Registered',
                  detail.cacRegister.registrationDate
                    ? dateOnly(detail.cacRegister.registrationDate)
                    : null,
                ],
                /*
                 * Stated either way, never left blank.
                 *
                 * A struck-off company stays findable on the register
                 * forever, so "not active" is a fact a reviewer needs in
                 * front of them rather than an absence they might read as
                 * "no answer".
                 */
                [
                  'Still on the register',
                  detail.cacRegister.isActive
                    ? 'Yes'
                    : 'No \u2014 struck off or dormant',
                ],
              ]}
            />
            {/*
              When it was read, said plainly rather than as a bare date. A
              filing read three months ago is evidence about three months
              ago, and that is the part a timestamp alone lets somebody skip.
            */}
            <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--neutral-600)' }}>
              Read from the register{' '}
              {formatDistanceToNow(new Date(detail.cacRegister.fetchedAt), {
                addSuffix: true,
              })}
              , on {dateOnly(detail.cacRegister.fetchedAt)}. It has not been
              re-read since: this is the filing as it stood at the claim,
              which is what the decision was made against.
            </p>

            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 600, color: 'var(--neutral-700)' }}>
              On the filing ({detail.cacRegister.directors.length})
            </div>
            {detail.cacRegister.directors.length === 0 ? (
              <Muted>
                The register named nobody who controls this company. Worth a
                second look: an empty board is usually a filing stored while
                the provider was returning no affiliate types, not a company
                without directors.
              </Muted>
            ) : (
              detail.cacRegister.directors.map((d) => (
                <div
                  key={`${d.fullName}-${d.boardRole ?? ''}`}
                  style={{
                    borderTop: '1px solid var(--neutral-300)',
                    padding: '10px 0',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{d.fullName}</div>
                  <div style={{ fontSize: 12, color: 'var(--neutral-600)', marginTop: 3 }}>
                    {[
                      d.boardRole,
                      d.nationality,
                      d.dateOfBirth
                        ? `b. ${format(new Date(d.dateOfBirth), 'd MMM yyyy')}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' \u00b7 ')}
                  </div>
                </div>
              ))
            )}
          </>
        ) : (
          <Muted>
            No filing stored. Either this business never claimed a
            registration, or it claimed one before the register was kept.
          </Muted>
        )}
      </Section>

      <Section title="Money">
        <Facts
          rows={[
            // Spendable, and the same figure the customer is looking at.
            // This used to be a ledger sum that ignored fees entirely, so it
            // sat above the app's by every fee the business had ever paid —
            // and support and the customer quoted different numbers.
            ['Available', naira(detail.money.balance)],
            ['At the bank', naira(detail.money.settledBalance)],
            [
              'On its way out',
              detail.money.pendingOut > 0
                ? naira(detail.money.pendingOut)
                : null,
            ],
            [
              // Said out loud rather than left to be inferred from a figure
              // that looks plausible either way.
              'Balance source',
              detail.money.balanceSource === 'bank'
                ? null
                : "Safe Haven didn't answer — this is the ledger's figure",
            ],
            ['Account number', detail.money.account?.accountNumber ?? null],
            ['Account name', detail.money.account?.accountName ?? null],
            [
              'In whose name',
              detail.money.account
                ? detail.money.account.kind === 'corporate'
                  ? 'The company (CAC registration)'
                  : "The director (opened against their BVN)"
                : null,
            ],
            ['Opened', detail.money.account ? dateOnly(detail.money.account.openedAt) : null],
          ]}
        />
      </Section>

      <Section title={`Directors (${detail.directors.length})`}>
        {detail.directors.length === 0 ? (
          <Muted>Nobody on the register yet.</Muted>
        ) : (
          detail.directors.map((d) => (
            <div
              key={d.id}
              style={{
                borderTop: '1px solid var(--neutral-300)',
                padding: '10px 0',
                fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ fontWeight: 600 }}>{d.fullName}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {/* Which names are supposed to be on a board resolution.
                      This used to be readable from a shareholding; the
                      register records none, so it is stated. */}
                  {!d.isDirector && <Badge variant="neutral">Shareholder</Badge>}
                  {d.isSignatory && <Badge variant="info">Signatory</Badge>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--neutral-600)', marginTop: 3 }}>
                {[
                  d.boardRole,
                  d.dateOfBirth
                    ? `b. ${format(new Date(d.dateOfBirth), 'd MMM yyyy')}`
                    : null,
                  // Last 4 only — a support screen is not a reason to put a
                  // national identifier on a page.
                  d.bvnLast4 ? `BVN ••••${d.bvnLast4}` : null,
                  d.idType && d.idLast4 ? `${d.idType} ••••${d.idLast4}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {d.residentialAddress && (
                <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
                  {d.residentialAddress}
                </div>
              )}
              {d.bvn && (
                <div style={{ marginTop: 6 }}>
                  <BvnProviders
                    dojah={d.bvn.dojah.status}
                    safehaven={d.bvn.safehaven}
                    failureReason={d.bvn.dojah.failureReason}
                    lookupProvider={d.bvn.dojah.provider}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </Section>

      <Section title={`Team (${detail.team.length})`}>
        {detail.team.length === 0 ? (
          <Muted>No members.</Muted>
        ) : (
          detail.team.map((m) => (
            <div
              key={m.id}
              style={{
                borderTop: '1px solid var(--neutral-300)',
                padding: '10px 0',
                fontSize: 13,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{m.name || m.email || m.userId}</div>
                <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                  {[m.jobTitle, m.email].filter(Boolean).join(' · ')}
                </div>
              </div>
              <Badge variant={m.status === 'active' ? 'success' : 'neutral'}>
                {m.status}
              </Badge>
            </div>
          ))
        )}
      </Section>

      <Section title={`Payment requests (${detail.paymentRequests.length})`}>
        {detail.paymentRequests.length === 0 ? (
          <Muted>None raised.</Muted>
        ) : (
          detail.paymentRequests.map((p) => (
            <Row
              key={p.id}
              left={p.customerName ?? 'Payment link'}
              sub={`${p.settlesTo === 'wallet' ? 'Pooled wallet' : 'Own account'} · ${dateTime(p.createdAt)}`}
              right={naira(p.amount)}
              badge={p.status}
            />
          ))
        )}
      </Section>

      <Section title="Recent transactions">
        {detail.transactions.length === 0 ? (
          <Muted>Nothing has moved yet.</Muted>
        ) : (
          detail.transactions.map((t) => (
            <Row
              key={t.id}
              left={t.counterparty ?? 'Unknown'}
              sub={`${t.narration ?? t.reference} · ${dateTime(t.createdAt)}`}
              // Direction from `type`, never from the sign of an amount.
              right={`${t.type === 'credit' ? '+' : '−'}${naira(t.amount)}`}
              rightColor={
                t.type === 'credit' ? 'var(--green-900)' : 'var(--neutral-900)'
              }
              badge={t.status !== 'success' ? t.status : undefined}
            />
          ))
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4
        style={{
          margin: '0 0 10px',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--neutral-600)',
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

/** Rows with nothing in them are dropped rather than rendered as blanks —
 *  an empty field reads as a system that lost something. */
function Facts({ rows }: { rows: [string, string | null | undefined][] }) {
  const present = rows.filter(([, v]) => v);
  return (
    <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
      {present.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', gap: 16 }}>
          <span style={{ width: 140, flexShrink: 0, color: 'var(--neutral-600)' }}>
            {label}
          </span>
          <span style={{ color: 'var(--neutral-900)', wordBreak: 'break-word' }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

function Row({
  left,
  sub,
  right,
  rightColor,
  badge,
}: {
  left: string;
  sub: string;
  right: string;
  rightColor?: string;
  badge?: string;
}) {
  return (
    <div
      style={{
        borderTop: '1px solid var(--neutral-300)',
        padding: '10px 0',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{left}</div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--neutral-600)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {sub}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontWeight: 600, color: rightColor ?? 'var(--neutral-900)' }}>
          {right}
        </div>
        {badge && (
          <div style={{ fontSize: 11, color: 'var(--neutral-600)' }}>{badge}</div>
        )}
      </div>
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>{children}</p>
  );
}
